import React, { lazy, Suspense, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import { CenteredSpinner } from '../modules/Base/components/Spinner'
import { RouterPaths } from './RouterPathsMapper'

import { ModalUtil } from '@/utils/modalUtil'
import { isTesting } from '@/utils/testing'

/**
 * Telas do drawer sao carregadas sob demanda.
 *
 * Estes 40 imports eram estaticos e, sozinhos, arrastavam para o chunk inicial todo o
 * `@react-pdf/renderer` (+pdfkit/reconciler/fontkit), `recharts`, `@visx/*`, `@mui/*`,
 * `react-beautiful-dnd`, `swiper` e `moment` — nada disso e necessario para o primeiro frame,
 * que e o mapa. O fallback do Suspense cobre apenas a area do drawer.
 */
const AdhpInfoPresenter = lazy(() =>
  import('../modules/AdhpInfo/containers/AdhpInfoPresenter').then((m) => ({ default: m.AdhpInfoPresenter }))
)
const AircraftPreCreation = lazy(() =>
  import('../modules/AircraftScreen/components/AircraftPreCreation').then((m) => ({ default: m.AircraftPreCreation }))
)
const AircraftRegisterScreen = lazy(() =>
  import('../modules/AircraftScreen/components/AircraftRegisterScreen').then((m) => ({
    default: m.AircraftRegisterScreen
  }))
)
const WBBasicEmptyWeight = lazy(() =>
  import('../modules/AircraftScreen/components/WBProfile/WBBasicEmptyWeight/WBBasicEmptyWeight').then((m) => ({
    default: m.WBBasicEmptyWeight
  }))
)
const WBCargo = lazy(() =>
  import('../modules/AircraftScreen/components/WBProfile/WBCargo/WBCargo').then((m) => ({ default: m.WBCargo }))
)
const WBChooseTemplate = lazy(() =>
  import('../modules/AircraftScreen/components/WBProfile/WBChooseTemplate/WBChooseTemplate').then((m) => ({
    default: m.WBChooseTemplate
  }))
)
const WBCreationMode = lazy(() =>
  import('../modules/AircraftScreen/components/WBProfile/WBCreationMode/WBCreationMode').then((m) => ({
    default: m.WBCreationMode
  }))
)
const WBFlightEnvelope = lazy(() =>
  import('../modules/AircraftScreen/components/WBProfile/WBFlightEnvelope/WBFlightEnvelope').then((m) => ({
    default: m.WBFlightEnvelope
  }))
)
const WBMAC = lazy(() =>
  import('../modules/AircraftScreen/components/WBProfile/WBMAC/WBMAC').then((m) => ({ default: m.WBMAC }))
)
const WBMaximumWeights = lazy(() =>
  import('../modules/AircraftScreen/components/WBProfile/WBMaximumWeights/WBMaximumWeights').then((m) => ({
    default: m.WBMaximumWeights
  }))
)
const WBSeats = lazy(() =>
  import('../modules/AircraftScreen/components/WBProfile/WBSeats/WBSeats').then((m) => ({ default: m.WBSeats }))
)
const WBTank = lazy(() =>
  import('../modules/AircraftScreen/components/WBProfile/WBTank/WBTank').then((m) => ({ default: m.WBTank }))
)
const WBUnits = lazy(() =>
  import('../modules/AircraftScreen/components/WBProfile/WBUnits/WBUnits').then((m) => ({ default: m.WBUnits }))
)
const WBUseAnotherTemplate = lazy(() =>
  import('../modules/AircraftScreen/components/WBProfile/WBUseAnotherTemplate/WBUseAnotherTemplate').then((m) => ({
    default: m.WBUseAnotherTemplate
  }))
)
const AircraftScreenPresenter = lazy(() =>
  import('../modules/AircraftScreen/containers/AircraftScreenPresenter').then((m) => ({
    default: m.AircraftScreenPresenter
  }))
)
const WBProfileStepsPresenter = lazy(() =>
  import('../modules/AircraftScreen/containers/WBProfileStepsPresenter').then((m) => ({
    default: m.WBProfileStepsPresenter
  }))
)
const BetaScreenPresenter = lazy(() =>
  import('../modules/BetaScreen/containers/BetaScreenPresenter').then((m) => ({ default: m.BetaScreenPresenter }))
)
const ChartsAndDataScreenPresenter = lazy(() =>
  import('../modules/ChartsAndDataScreen/containers/ChartsAndDataPresenter').then((m) => ({
    default: m.ChartsAndDataScreenPresenter
  }))
)
const ConfigurationScreenPresenter = lazy(() =>
  import('../modules/ConfigurationScreen/containers/ConfigurationScreenPresenter').then((m) => ({
    default: m.ConfigurationScreenPresenter
  }))
)
const MarketScreenPresenter = lazy(() =>
  import('../modules/MarketScreen/containers/MarketScreenPresenter').then((m) => ({ default: m.MarketScreenPresenter }))
)
const MobileAppScreenPresenter = lazy(() =>
  import('../modules/MobileAppScreen/containers/MobileAppScreenPresenter').then((m) => ({
    default: m.MobileAppScreenPresenter
  }))
)
const MyContentScreenPresenter = lazy(() =>
  import('../modules/MyContentScreen/containers/MyContentScreenPresenter').then((m) => ({
    default: m.MyContentScreenPresenter
  }))
)
const NewsScreenPresenter = lazy(() =>
  import('../modules/NewsScreen/containers/NewsScreenPresenter').then((m) => ({ default: m.NewsScreenPresenter }))
)
const ProceduresScreenPresenter = lazy(() =>
  import('../modules/ProceduresScreen/ProceduresScreenPresenter').then((m) => ({
    default: m.ProceduresScreenPresenter
  }))
)
const RoutesPanelScreenPresenter = lazy(() =>
  import('../modules/RoutesPanelScreen/containers/RoutesPanelScreenPresenter').then((m) => ({
    default: m.RoutesPanelScreenPresenter
  }))
)
const RouteWaypointInsertionModePresenter = lazy(() =>
  import('../modules/RouteWaypointInsertionModeScreen/containers/RouteWaypointInsertionModePresenter').then((m) => ({
    default: m.RouteWaypointInsertionModePresenter
  }))
)
const SavedRoutesScreenPresenter = lazy(() =>
  import('../modules/SavedRoutesScreen/containers/SavedRoutesScreenPresenter').then((m) => ({
    default: m.SavedRoutesScreenPresenter
  }))
)
const UserChartsScreenPresenter = lazy(() =>
  import('../modules/UserChartsScreen/containers/UserChartsScreenPresenter').then((m) => ({
    default: m.UserChartsScreenPresenter
  }))
)
const UserChartEditionScreenPresenter = lazy(() =>
  import('../modules/UserChartsScreen/UserChartEdition/UserChartEditionScreenPresenter').then((m) => ({
    default: m.UserChartEditionScreenPresenter
  }))
)
const UserChartUploadScreenPresenter = lazy(() =>
  import('../modules/UserChartsScreen/UserChartUpload/UserChartUploadScreenPresenter').then((m) => ({
    default: m.UserChartUploadScreenPresenter
  }))
)
const UserGeometriesScreenPresenter = lazy(() =>
  import('../modules/UserGeometriesScreen/containers/UserGeometriesScreenPresenter').then((m) => ({
    default: m.UserGeometriesScreenPresenter
  }))
)
const UserGeometryEditionScreenPresenter = lazy(() =>
  import('../modules/UserGeometriesScreen/Edition/UserGeometryEditionScreenPresenter').then((m) => ({
    default: m.UserGeometryEditionScreenPresenter
  }))
)
const UserGeometryUploadScreenPresenter = lazy(() =>
  import('../modules/UserGeometriesScreen/UserGeometryUpload/UserGeometryUploadScreenPresenter').then((m) => ({
    default: m.UserGeometryUploadScreenPresenter
  }))
)
const UserWaypointsScreenPresenter = lazy(() =>
  import('../modules/UserWaypointsScreen/containers/UserWaypointsScreenPresenter').then((m) => ({
    default: m.UserWaypointsScreenPresenter
  }))
)
const VNavScreenPresenter = lazy(() =>
  import('../modules/VNavScreen/containers/VNavScreenPresenter').then((m) => ({ default: m.VNavScreenPresenter }))
)
const DefaultWeightsManagement = lazy(() =>
  import('../modules/WeightAndBalanceScreen/components/DefaultWeightsManagement/DefaultWeightsManagement').then(
    (m) => ({ default: m.DefaultWeightsManagement })
  )
)
const WBFillingCreationMode = lazy(() =>
  import('../modules/WeightAndBalanceScreen/components/WBFillingCreationMode').then((m) => ({
    default: m.WBFillingCreationMode
  }))
)
const WBFlightSetup = lazy(() =>
  import('../modules/WeightAndBalanceScreen/components/WBFlightSetup/WBFlightSetup').then((m) => ({
    default: m.WBFlightSetup
  }))
)
const WBFlightFillingPresenter = lazy(() =>
  import('../modules/WeightAndBalanceScreen/containers/WBAirplaneFlightFilling/WBAirplaneFlightFillingPresenter').then(
    (m) => ({ default: m.WBFlightFillingPresenter })
  )
)
const WeightAndBalanceScreenPresenter = lazy(() =>
  import('../modules/WeightAndBalanceScreen/containers/WeightAndBalanceScreenPresenter').then((m) => ({
    default: m.WeightAndBalanceScreenPresenter
  }))
)

interface ChangeRouteEventDetail {
  screen: string
  stateLocation?: Record<string, string>
}

type RouteEvent = CustomEvent<ChangeRouteEventDetail>

export const openDrawer = (screen: string, stateLocation?: Record<string, string>) => {
  const evento = new CustomEvent<ChangeRouteEventDetail>('change-route', {
    detail: {
      screen,
      stateLocation
    }
  })

  document.dispatchEvent(evento)
}

export const closeDrawer = () => {
  const evento = new CustomEvent<ChangeRouteEventDetail>('change-route', {
    detail: {
      screen: RouterPaths.MAP
    }
  })

  document.dispatchEvent(evento)
}

const RedirectToMap = (): React.ReactElement | null => {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    ModalUtil.global.show({
      title: 'Página não encontrada', // TODO: i18n
      headerDescription:
        'A página que você está tentando acessar não existe ou foi removida. Você será redirecionado para o mapa.',
      maxWidth: 400,
      onPositive: () => {}
    })
    navigate(RouterPaths.MAP)
  }, [navigate])

  return null
}

function RemoveTrailingSlash(): React.ReactElement | null {
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (location.pathname !== '/' && location.pathname.endsWith('/')) {
      navigate(location.pathname.slice(0, -1) + location.search, { replace: true })
    }
  }, [location, navigate])

  return null
}

function DrawerRouter() {
  const navigate = useNavigate()

  useEffect(() => {
    const changeRoute = (e: RouteEvent) => {
      navigate(e.detail.screen, { state: e.detail.stateLocation })
    }
    const routeChangeListener = (e: Event) => changeRoute(e as RouteEvent)
    document.addEventListener('change-route', routeChangeListener)

    return () => {
      document.removeEventListener('change-route', routeChangeListener)
    }
  }, [navigate])

  return (
    <>
      <RemoveTrailingSlash />
      <Suspense fallback={<CenteredSpinner />}>
        <Routes>
          <Route path="/" element={null} />
          <Route path="*" element={<RedirectToMap />} />
          <Route path={RouterPaths.MAP} element={null} />

          <Route path={RouterPaths.AIRCRAFT} element={<AircraftScreenPresenter />} />

          {/* Criação de aeronave */}
          <Route path={RouterPaths.AIRCRAFT_NEW}>
            <Route index element={<AircraftPreCreation />} />
            <Route path={RouterPaths.AIRCRAFT_REGISTER} element={<AircraftRegisterScreen action="create" />} />

            {/* Criar novo perfil de P&B */}
            <Route path={RouterPaths.WB_PROFILES_NEW}>
              <Route index path={RouterPaths.WB_CREATION_MODE} element={<WBCreationMode />} />
              <Route path={RouterPaths.USE_ANOTHER_TEMPLATE} element={<WBUseAnotherTemplate />} />
              <Route path={RouterPaths.WB_CHOOSE_TEMPLATE} element={<WBChooseTemplate />} />
              <Route path={RouterPaths.WB_UNITS} element={<WBUnits />} />
              <Route path={RouterPaths.WB_STEPS} element={<WBProfileStepsPresenter />}>
                <Route index path={RouterPaths.WB_MAC} element={<WBMAC />} />
                <Route path={RouterPaths.WB_BASIC_EMPTY_WEIGHT} element={<WBBasicEmptyWeight />} />
                <Route path={RouterPaths.WB_SEATS} element={<WBSeats />} />
                <Route path={RouterPaths.WB_CARGO} element={<WBCargo />} />
                <Route path={RouterPaths.WB_TANK} element={<WBTank />} />
                <Route path={RouterPaths.WB_MAXIMUM_WEIGHTS} element={<WBMaximumWeights />} />
                <Route path={RouterPaths.WB_ENVELOPE} element={<WBFlightEnvelope />} />
              </Route>
            </Route>

            {/* Editar perfil de P&B existente */}
            <Route path={RouterPaths.WB_PROFILES_EDIT}>
              <Route path={RouterPaths.WB_CREATION_MODE} element={<WBCreationMode />} />
              <Route path={RouterPaths.USE_ANOTHER_TEMPLATE} element={<WBUseAnotherTemplate />} />
              <Route path={RouterPaths.WB_CHOOSE_TEMPLATE} element={<WBChooseTemplate />} />
              <Route path={RouterPaths.WB_UNITS} element={<WBUnits />} />
              <Route path={RouterPaths.WB_STEPS} element={<WBProfileStepsPresenter />}>
                <Route index path={RouterPaths.WB_MAC} element={<WBMAC />} />
                <Route path={RouterPaths.WB_BASIC_EMPTY_WEIGHT} element={<WBBasicEmptyWeight />} />
                <Route path={RouterPaths.WB_SEATS} element={<WBSeats />} />
                <Route path={RouterPaths.WB_CARGO} element={<WBCargo />} />
                <Route path={RouterPaths.WB_TANK} element={<WBTank />} />
                <Route path={RouterPaths.WB_MAXIMUM_WEIGHTS} element={<WBMaximumWeights />} />
                <Route path={RouterPaths.WB_ENVELOPE} element={<WBFlightEnvelope />} />
              </Route>
            </Route>
          </Route>

          {/* Edição de aeronave existente */}
          <Route path={RouterPaths.AIRCRAFT_EDIT}>
            <Route path={RouterPaths.AIRCRAFT_REGISTER} element={<AircraftRegisterScreen action="edit" />} />

            {/* Criar novo perfil de P&B */}
            <Route path={RouterPaths.WB_PROFILES_NEW}>
              <Route index path={RouterPaths.WB_CREATION_MODE} element={<WBCreationMode />} />
              <Route path={RouterPaths.USE_ANOTHER_TEMPLATE} element={<WBUseAnotherTemplate />} />
              <Route path={RouterPaths.WB_CHOOSE_TEMPLATE} element={<WBChooseTemplate />} />
              <Route path={RouterPaths.WB_UNITS} element={<WBUnits />} />
              <Route path={RouterPaths.WB_STEPS} element={<WBProfileStepsPresenter />}>
                <Route index path={RouterPaths.WB_MAC} element={<WBMAC />} />
                <Route path={RouterPaths.WB_BASIC_EMPTY_WEIGHT} element={<WBBasicEmptyWeight />} />
                <Route path={RouterPaths.WB_SEATS} element={<WBSeats />} />
                <Route path={RouterPaths.WB_CARGO} element={<WBCargo />} />
                <Route path={RouterPaths.WB_TANK} element={<WBTank />} />
                <Route path={RouterPaths.WB_MAXIMUM_WEIGHTS} element={<WBMaximumWeights />} />
                <Route path={RouterPaths.WB_ENVELOPE} element={<WBFlightEnvelope />} />
              </Route>
            </Route>

            {/* Editar perfil de P&B existente */}
            <Route path={RouterPaths.WB_PROFILES_EDIT}>
              <Route index path={RouterPaths.WB_UNITS} element={<WBUnits />} />
              <Route path={RouterPaths.WB_STEPS} element={<WBProfileStepsPresenter />}>
                <Route index path={RouterPaths.WB_MAC} element={<WBMAC />} />
                <Route path={RouterPaths.WB_BASIC_EMPTY_WEIGHT} element={<WBBasicEmptyWeight />} />
                <Route path={RouterPaths.WB_SEATS} element={<WBSeats />} />
                <Route path={RouterPaths.WB_CARGO} element={<WBCargo />} />
                <Route path={RouterPaths.WB_TANK} element={<WBTank />} />
                <Route path={RouterPaths.WB_MAXIMUM_WEIGHTS} element={<WBMaximumWeights />} />
                <Route path={RouterPaths.WB_ENVELOPE} element={<WBFlightEnvelope />} />
              </Route>
            </Route>
          </Route>

          <Route path={RouterPaths.ROUTES_PANEL} element={<RoutesPanelScreenPresenter />} />

          <Route path={RouterPaths.WEIGHT_AND_BALANCE}>
            <Route index element={<WeightAndBalanceScreenPresenter />} />
            <Route path={RouterPaths.DEFAULT_WEIGHTS_MANAGEMENT} element={<DefaultWeightsManagement />} />
            {/* Novo preenchimento */}
            <Route path={RouterPaths.WEIGHT_AND_BALANCE_NEW}>
              <Route path={RouterPaths.WEIGHT_AND_BALANCE_CREATION_MODE} element={<WBFillingCreationMode />} />
              <Route path={RouterPaths.WB_FLIGHT_SETUP} element={<WBFlightSetup />} />
              <Route path={RouterPaths.WB_FLIGHT_FILLING} element={<WBFlightFillingPresenter />} />
            </Route>
            {/* Editando preenchimento */}
            <Route path={RouterPaths.WEIGHT_AND_BALANCE_EDIT}>
              <Route index path={RouterPaths.WB_FLIGHT_SETUP} element={<WBFlightSetup />} />
              <Route path={RouterPaths.WB_FLIGHT_FILLING} element={<WBFlightFillingPresenter />} />
            </Route>
          </Route>

          <Route path={RouterPaths.VERTICAL_NAVIGATION} element={<VNavScreenPresenter />} />
          <Route path={RouterPaths.SAVED_ROUTES} element={<SavedRoutesScreenPresenter />} />
          <Route path={RouterPaths.PROCEDURE_CHARTS} element={<ProceduresScreenPresenter />} />
          <Route path={RouterPaths.ADHP_INFO} element={<AdhpInfoPresenter />} />
          <Route path={RouterPaths.MARKET} element={<MarketScreenPresenter />} />
          <Route path={RouterPaths.BETA} element={isTesting() ? <BetaScreenPresenter /> : <RedirectToMap />} />
          <Route path={RouterPaths.MOBILE} element={<MobileAppScreenPresenter />} />
          <Route path={RouterPaths.NEWS} element={<NewsScreenPresenter />} />
          <Route path={RouterPaths.SETTINGS} element={<ConfigurationScreenPresenter />}>
            <Route
              path={RouterPaths.SETTINGS__ROUTE_WAYPOINT_INSERTION_MODE}
              element={<RouteWaypointInsertionModePresenter />}
            />
            <Route path={RouterPaths.SETTINGS__DATA_AND_CHARTS} element={<ChartsAndDataScreenPresenter />} />
          </Route>

          <Route path={RouterPaths.MY_CONTENT} element={<MyContentScreenPresenter />}>
            <Route index element={<Navigate to={RouterPaths.MY_CONTENT__USER_WAYPOINTS} replace />} />
            <Route path={RouterPaths.MY_CONTENT__USER_WAYPOINTS} element={<UserWaypointsScreenPresenter />}>
              <Route path={RouterPaths.MY_CONTENT__USER_WAYPOINTS__CREATE} element={<UserWaypointsScreenPresenter />} />
              <Route path={RouterPaths.MY_CONTENT__USER_WAYPOINTS__EDIT} element={<UserWaypointsScreenPresenter />} />
            </Route>
            <Route path={RouterPaths.MY_CONTENT__USER_CHARTS} element={<UserChartsScreenPresenter />} />
            <Route
              path={`${RouterPaths.MY_CONTENT__USER_CHARTS}/${RouterPaths.MY_CONTENT__USER_CHARTS__UPLOAD}`}
              element={<UserChartUploadScreenPresenter />}
            />
            <Route
              path={`${RouterPaths.MY_CONTENT__USER_CHARTS}/${RouterPaths.MY_CONTENT__USER_CHARTS__EDIT}`}
              element={<UserChartEditionScreenPresenter />}
            />
            <Route path={RouterPaths.MY_CONTENT__USER_GEOMETRIES} element={<UserGeometriesScreenPresenter />} />
            <Route
              path={`${RouterPaths.MY_CONTENT__USER_GEOMETRIES}/${RouterPaths.MY_CONTENT__USER_GEOMETRIES__UPLOAD}`}
              element={<UserGeometryUploadScreenPresenter />}
            />
            <Route
              path={`${RouterPaths.MY_CONTENT__USER_GEOMETRIES}/${RouterPaths.MY_CONTENT__USER_GEOMETRIES__EDIT}`}
              element={<UserGeometryEditionScreenPresenter />}
            />
          </Route>
        </Routes>
      </Suspense>
    </>
  )
}

export { DrawerRouter }
